import { CommonModule } from '@angular/common';
import { Component, computed, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { FeatureStore } from '@app/_stores/feature.store';
import { ViewSettingsStore } from '@app/_stores/view-settings.store';
import { MessageStore } from '@app/_stores';
import { SvgComponent } from '../svg/svg.component';
import { MobileAddEntitiesListComponent } from './mobile-add-entities-list/mobile-add-entities-list.component';
import { MeoStore } from '@app/_stores/meo.store';
import { LongPressDirective } from '../../../directives/long-press.directive';

interface NavItem {
  id: string;
  icon: string;
  label: string;
  route: string;
}

@Component({
  selector: 'app-mobile-bottom-bar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    SvgComponent,
    MobileAddEntitiesListComponent,
    LongPressDirective
  ],
  templateUrl: './mobile-bottom-bar.component.html',
  styleUrl: './mobile-bottom-bar.component.scss'
})
export class MobileBottomBarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly viewSettings = inject(ViewSettingsStore);
  private readonly featureStore = inject(FeatureStore);
  readonly messageStore = inject(MessageStore);
  readonly ss = inject(SettingsService);
  readonly cc = inject(CacheService);
  readonly meoStore = inject(MeoStore);

  private routerSub?: Subscription;

  // State
  isAddMode = signal(false);
  isVisible = signal(true);
  isOnTodayPage = signal(false);
  isOnPastOrFuturePage = signal(false);

  /** Sidebar pushes content on desktop (>= 992px). Bottom bar must shift with it. */
  isSidebarOpen = computed(() => this.ss.showSidebar() && this.cc.loggedIn());
  private lastScrollY = 0;

  /**
   * Build navigation items based on feature flags
   * Order: Lists, Past, Today (plus button), Future, Message
   * Following Flutter's MeBottomBar layout
   */
  navItems = computed((): NavItem[] => {
    const items: NavItem[] = [];

    // Lists (left side)
    if (this.viewSettings.showListFeature()) {
      items.push({
        id: 'lists',
        icon: 'list',
        label: this.ss.t('common_list') || 'List',
        route: '/lists'
      });
    }

    // Date-based items: Past, Today, Future
    if (this.featureStore.dateBasedEntities().length > 0) {
      items.push({
        id: 'past',
        icon: 'leftArrow',
        label: this.ss.t('navigationBar_past') || 'Past',
        route: '/past'
      });

      // Today is special - rendered as plus button in template
      items.push({
        id: 'today',
        icon: 'plus',
        label: this.ss.t('common_today') || 'Today',
        route: '/today'
      });

      items.push({
        id: 'future',
        icon: 'rightArrow',
        label: this.ss.t('navigationBar_future') || 'Future',
        route: '/future'
      });
    }

    // Messages (always shown, matching Flutter)
    items.push({
      id: 'messages',
      icon: 'messageBottom',
      label: this.ss.t('common_message'),
      route: '/messages'
    });

    return items;
  });

  // Only show if more than 1 item enabled
  shouldShow = computed(() => this.navItems().length > 1);

  /** Unread message badge text. Shows count or "9+" when >9. Empty when 0. */
  unreadBadgeText = computed(() => {
    const count = this.messageStore.unreadCount();
    if (count <= 0) return '';
    return count > 9 ? '9+' : `${count}`;
  });

  ngOnInit(): void {
    // Check initial route
    this.updateTodayPageStatus(this.router.url);

    // Track route changes
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        this.isAddMode.set(false);
        this.updateTodayPageStatus(event.urlAfterRedirects);
      });
  }

  /**
   * Check if current URL is the today page
   */
  private updateTodayPageStatus(url: string): void {
    const isOnToday = url === '/today' ||
                      url.startsWith('/today?') ||
                      url.startsWith('/today/');
    this.isOnTodayPage.set(isOnToday);

    const isOnPastOrFuture = url === '/past' || url.startsWith('/past?') || url.startsWith('/past/') ||
                             url === '/future' || url.startsWith('/future?') || url.startsWith('/future/');
    this.isOnPastOrFuturePage.set(isOnPastOrFuture);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  // Scroll hide/show behavior (matches Flutter MeBottomBar)
  @HostListener('window:scroll')
  onScroll(): void {
    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > this.lastScrollY;

    // Only hide if scrolling down AND not at top
    if (isScrollingDown && currentScrollY > 50) {
      this.isVisible.set(false);
    } else {
      this.isVisible.set(true);
    }

    this.lastScrollY = currentScrollY;
  }

  /**
   * Handle Today plus button click
   * Toggle add mode to show/hide add entities list
   */
  onTodayClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // First navigate to Today if not already there
    const isOnTodayPage = this.router.url === '/today' ||
                          this.router.url.startsWith('/today?') ||
                          this.router.url.startsWith('/today/');

    if (!isOnTodayPage) {
      this.router.navigate(['/today']);
    }

    // Toggle add mode
    this.isAddMode.update(v => !v);
  }

  /**
   * Handle click outside the add entities list to close it
   */
  onClickOutside(): void {
    if (this.isAddMode()) {
      this.isAddMode.set(false);
    }
  }

  resetAddMode(): void {
    this.isAddMode.set(false);
  }

  onAiFabClick(): void {
    this.meoStore.openPanel();
  }

  onAiFabLongPress(): void {
    this.meoStore.openPanel(true); // autoStartMic = true
  }
}
